import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
} from '@mui/material';

interface NewVersionConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (notes: string) => void;
  versionNumber: number;
}

const NewVersionConfirmationDialog: React.FC<NewVersionConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  versionNumber,
}) => {
  const [notes, setNotes] = useState('');

  // Reset when closed (e.g. after successful create — parent only sets open=false, not handleClose).
  useEffect(() => {
    if (!open) {
      setNotes('');
    }
  }, [open]);

  const handleConfirm = () => {
    // Notes are optional, but API requires a string, so send empty string if not provided
    onConfirm(notes.trim() || '');
  };

  const handleClose = () => {
    setNotes('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Paint Data Version Confirmation</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 3 }}>
          Please confirm that you want to create a new version of Paint Data. Once confirmed, the
          pricing for the new version will go into effect immediately and affect all future orders.
          This will not affect any existing orders.
        </DialogContentText>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            New Version Details
          </Typography>

          <TextField
            fullWidth
            label="Version"
            value={versionNumber}
            disabled
            sx={{ mb: 2 }}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={4}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Enter notes about this version (optional)"
            inputProps={{
              maxLength: 500,
            }}
            helperText={`${notes.length}/500 characters`}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" color="primary">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewVersionConfirmationDialog;
